import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { authClient } from "@/lib/auth-client";

const searchSchema = z
	.object({
		client_id: z.string().optional(),
		scope: z.string().optional(),
	})
	.loose();

export const Route = createFileRoute("/oauth/consent")({
	component: OAuthConsentPage,
	validateSearch: searchSchema,
});

function OAuthConsentPage() {
	const search = Route.useSearch();

	const clientId = search.client_id ?? "an application";
	const scopes = (search.scope ?? "").split(/\s+/).filter(Boolean);

	const { mutate, isPending, error } = useMutation({
		mutationKey: ["oauth", "consent"],
		mutationFn: async (accept: boolean) => {
			const data = await authClient.oauth2.consent({ accept }, { throw: true });

			const target = data.url;

			if (!target) {
				throw new Error("Consent succeeded but the server returned no redirect URI.");
			}

			return target;
		},
		onSuccess: (target) => {
			window.location.assign(target);
		},
	});

	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 p-6">
			<div>
				<h1 className="text-2xl font-semibold">Authorize access</h1>
				<p className="mt-2 text-sm text-muted-foreground">
					<span className="font-medium">{clientId}</span> wants to access your StudyOS
					account.
				</p>
			</div>
			{scopes.length > 0 && (
				<div>
					<h2 className="text-sm font-medium">Requested permissions</h2>
					<ul className="mt-2 space-y-1 text-sm text-muted-foreground">
						{scopes.map((scope) => (
							<li key={scope}>· {scope}</li>
						))}
					</ul>
				</div>
			)}
			{error && (
				<div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error.message}
				</div>
			)}
			<div className="flex gap-2">
				<button
					type="button"
					disabled={isPending}
					onClick={() => mutate(true)}
					className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
				>
					{isPending ? "Authorizing…" : "Allow"}
				</button>
				<button
					type="button"
					disabled={isPending}
					onClick={() => mutate(false)}
					className="flex-1 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
				>
					Deny
				</button>
			</div>
		</div>
	);
}
